import { cn } from "cn";
import { isEmpty } from "lodash-es";
import { useMemo, useState } from "react";
import { Attachment } from "@/components/ui/attachment-new";
import type { AttachmentUploadHandle } from "@/libs/attachment-upload-handle";
import {
  type AttachmentSource,
  areAttachmentsPending,
  toAttachmentUploadStatus,
} from "@/libs/form-attachment";
import { LightBox } from "../lightbox";

type PreviewUploadAttachmentsProps = {
  uploadRef: React.RefObject<AttachmentUploadHandle | null>;
  attachments: AttachmentSource[];
  className?: string;
  action?: React.ReactNode;
  /** Each tile's width — a strip has no column for the tile to fill. */
  tileClassName?: string;
};

export const PreviewUploadAttachments = ({
  uploadRef,
  attachments,
  className,
  action,
  tileClassName = "w-21 shrink-0",
}: PreviewUploadAttachmentsProps) => {
  const [attachmentIndex, setAttachmentIndex] = useState<number | undefined>(
    undefined,
  );
  const attachmentsFiltered = useMemo(() => {
    return attachments
      .filter((attachment) => Number(attachment.fileSize) > 0)
      .map((attachment) => {
        return {
          ...attachment,
          originalContentUri:
            attachment.originalContentUri || attachment.contentUri || "",
          contentUri:
            attachment.originalContentUri || attachment.contentUri || "",
        };
      });
  }, [attachments]);

  if (isEmpty(attachments)) {
    return null;
  }
  return (
    <div
      className={cn(
        /* sorted */
        "flex overflow-x-auto",
        "items-center gap-1 flex-nowrap shrink-0",
        className,
      )}
    >
      {attachments.map((attachment, index) => {
        const status = toAttachmentUploadStatus(attachment.status);
        return (
          <div
            key={attachment.uid ?? attachment.id ?? ""}
            data-test="comment-item-edit-attachment-container"
            className={tileClassName}
          >
            <Attachment
              fileName={attachment.fileName || ""}
              originalContentUri={
                attachment.originalContentUri || attachment.contentUri
              }
              thumbnailUri={attachment.thumbnailUri}
              contentType={attachment.contentType}
              fileLayout="details"
              fileSize={attachment.fileSize}
              status={status}
              percent={attachment.percent ?? undefined}
              onClick={() => {
                if (areAttachmentsPending([{ status }])) return;
                setAttachmentIndex(index);
              }}
              onRemove={() => {
                uploadRef.current?.removeAttachment(attachment.id ?? "");
              }}
              onRetry={
                uploadRef.current?.retryAttachment
                  ? () => {
                      uploadRef.current?.retryAttachment?.(attachment.id ?? "");
                    }
                  : undefined
              }
            />
          </div>
        );
      })}
      {action}
      {attachmentsFiltered.length > 0 && (
        <LightBox
          open={attachmentIndex !== undefined}
          slideIndex={attachmentIndex}
          attachments={attachmentsFiltered}
          onClose={() => setAttachmentIndex(undefined)}
        />
      )}
    </div>
  );
};
